import { useT } from "@magic-md/i18n/react";
import { ImageOff } from "lucide-react";
import { useEffect, useState } from "react";
import { getPlatformService } from "../../platform";

interface ImagePreviewProps {
    /** Absolute file path (required for raster images). */
    filePath?: string;
    /** Inline source for SVG (text content). */
    svgSource?: string;
    /** "image" → raster from disk; "svg" → inline text. */
    kind: "image" | "svg";
}

/**
 * Renders raster images via `platform.getAssetUrl(filePath)` (Tauri asset://
 * protocol on desktop) and SVGs via inline `srcDoc` so content shows even
 * when the platform has no asset URL handler (Web/Play).
 */
export function ImagePreview({ filePath, svgSource, kind }: ImagePreviewProps) {
    const t = useT("editor");
    const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        setDims(null);
        setError(false);
    }, [filePath, svgSource]);

    if (kind === "svg") {
        if (!svgSource) {
            return <Empty message={t("preview.svgSourceEmpty")} />;
        }
        const dataUri = `data:image/svg+xml;utf8,${encodeURIComponent(svgSource)}`;
        return (
            <Frame label="SVG" sub={dims ? `${dims.w}×${dims.h}` : undefined}>
                <img
                    src={dataUri}
                    alt="SVG preview"
                    className="max-w-full max-h-full object-contain"
                    onLoad={(e) => {
                        const img = e.currentTarget;
                        setDims({ w: img.naturalWidth, h: img.naturalHeight });
                    }}
                    onError={() => setError(true)}
                />
                {error && (
                    <div className="absolute inset-0 flex items-center justify-center text-[11px] text-muted-foreground">
                        {t("preview.svgRenderFailed")}
                    </div>
                )}
            </Frame>
        );
    }

    const url = filePath ? getPlatformService().getAssetUrl?.(filePath) : undefined;
    if (!filePath) return <Empty message={t("preview.noFilePath")} />;
    if (!url) {
        return (
            <Empty
                message={t("preview.unsupportedImage")}
                hint={t("preview.openInDesktop")}
            />
        );
    }

    return (
        <Frame label={kind.toUpperCase()} sub={dims ? `${dims.w}×${dims.h}` : undefined}>
            <img
                src={url}
                alt={filePath}
                className="max-w-full max-h-full object-contain"
                onLoad={(e) => {
                    const img = e.currentTarget;
                    setDims({ w: img.naturalWidth, h: img.naturalHeight });
                }}
                onError={() => setError(true)}
            />
            {error && (
                <div className="absolute inset-0 flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
                    <ImageOff size={14} /> {t("preview.imageLoadFailed")}
                </div>
            )}
        </Frame>
    );
}

function Frame({
    label,
    sub,
    children,
}: {
    label: string;
    sub?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="h-full w-full flex flex-col bg-background">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-muted/40">
                <span className="text-[11px] font-medium">{label}</span>
                {sub && <span className="text-[10px] font-mono text-muted-foreground">{sub}</span>}
            </div>
            <div className="flex-1 relative flex items-center justify-center overflow-auto p-4 bg-[linear-gradient(45deg,rgba(0,0,0,0.04)_25%,transparent_25%,transparent_75%,rgba(0,0,0,0.04)_75%),linear-gradient(45deg,rgba(0,0,0,0.04)_25%,transparent_25%,transparent_75%,rgba(0,0,0,0.04)_75%)] bg-[length:16px_16px] bg-[position:0_0,8px_8px]">
                {children}
            </div>
        </div>
    );
}

function Empty({ message, hint }: { message: string; hint?: string }) {
    return (
        <div className="h-full flex flex-col items-center justify-center gap-1 text-muted-foreground">
            <ImageOff size={24} className="opacity-50" />
            <div className="text-[12px]">{message}</div>
            {hint && <div className="text-[10px] opacity-70">{hint}</div>}
        </div>
    );
}
